import { useEffect, useMemo, useState } from 'react';
import type { ChangeEvent } from 'react';
import type { IntakeShellRenderProps } from './IntakeShell.js';
import {
  ALL_MODULE_SLUGS,
  BASE_MODULE_SLUGS,
  MODULE_CATALOG,
  MODULE_GROUPS,
  MODULE_GROUP_DESCRIPTIONS,
  MODULE_GROUP_LABELS,
  describeActivationError,
  getModule,
  isModuleImplemented,
  resolveModuleActivation,
  validateDeactivation,
  type ModuleGroup,
  type ModuleSlug,
} from '../../../lib/tenant-modules-form.js';

/**
 * wd-2.3.9 — Step 8 "Module activation" for the prospect-facing intake
 * portal.
 *
 * Fields mirror `intakeStep8Schema` in `packages/config/src/intake-steps.ts`:
 *
 *   modules: string[] (≥5, must contain foundation set, no duplicates)
 *   dependencyValidated: literal true (prospect confirmation checkpoint)
 *   tenantRationale: Record<slug, string> (optional, per-module notes)
 *
 * The intake FOUNDATION SET is 5 modules — the 3 base modules plus crm
 * and messaging — all locked-on regardless of prospect toggling. Optional
 * modules (10) are checkbox-toggleable. Selecting an optional module
 * auto-activates its transitive deps via the same resolver the wizard
 * uses (mirrors the platform-truth resolver in
 * packages/core/tenant/activation.ts). Clearing an optional module is
 * blocked if another requested optional still depends on it.
 *
 * `dependencyValidated` starts false. After the prospect clicks "Confirm
 * my module selection" the flag flips true and the step becomes
 * submittable. Any subsequent toggle clears the flag — any change
 * requires a fresh acknowledgement.
 *
 * Rationale rows are free-text notes keyed by module slug. Empty values
 * are stripped before serialization so `z.record(z.string(),
 * z.string())` never sees phantom empty-string entries.
 */

// Foundation set per intakeStep8Schema. Superset of BASE_MODULE_SLUGS.
const FOUNDATION_SLUGS: ReadonlyArray<ModuleSlug> = [
  'tenant-config',
  'auth',
  'rbac',
  'crm',
  'messaging',
];
const FOUNDATION_SET: ReadonlySet<ModuleSlug> = new Set(FOUNDATION_SLUGS);

const OPTIONAL_SLUGS: ReadonlyArray<ModuleSlug> = ALL_MODULE_SLUGS.filter(
  (s) => !FOUNDATION_SET.has(s),
);

const MAX_RATIONALE_LEN = 280;

// Default-active additions on first mount for pet-services prospects.
// CRM and Messaging are already in the foundation, so the only extras
// here are booking + payments — the two modules every HTHD-shaped shop
// needs out of the box. Rest stay off until the prospect opts in.
const DEFAULT_OPTIONAL_ADDITIONS: ReadonlyArray<ModuleSlug> = [
  'booking',
  'payments',
];

type Step8Draft = {
  /** User's explicit optional selections — excludes foundation and auto-deps. */
  requestedOptional: ReadonlySet<ModuleSlug>;
  /** Confirmation flag — prospect has acknowledged the auto-activated deps. */
  dependencyValidated: boolean;
  /** Local rationale rows so empty values don't leak into the draft. */
  rationale: Map<ModuleSlug, string>;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readModulesArray(value: unknown): Set<ModuleSlug> {
  const out = new Set<ModuleSlug>();
  if (!Array.isArray(value)) return out;
  for (const raw of value) {
    if (typeof raw !== 'string') continue;
    const entry = getModule(raw);
    if (!entry) continue;
    // #162 — sanitize on load: a coming-soon slug present in a restored or
    // hand-crafted draft (e.g. saved before this gate shipped) must NOT
    // survive into the requested set or the emitted payload. The disabled
    // checkbox + handleToggle guard only block new clicks; this closes the
    // persistence path so the intake side matches the platform `readSlugList`.
    if (!entry.implemented) continue;
    out.add(entry.slug);
  }
  return out;
}

function readRationale(value: unknown): Map<ModuleSlug, string> {
  const out = new Map<ModuleSlug, string>();
  if (!isRecord(value)) return out;
  for (const [slug, text] of Object.entries(value)) {
    const entry = getModule(slug);
    if (!entry) continue;
    if (typeof text !== 'string') continue;
    out.set(entry.slug, text);
  }
  return out;
}

/**
 * Given a set of user optional requests, compute the full closure:
 * foundation + requested + transitive deps, topologically sorted with
 * deps first. Falls back to foundation-only if the resolver rejects
 * (shouldn't happen with the acyclic v1 catalog but keeps the UI alive
 * if the catalog ever grows a cycle).
 */
function computeFinalSet(
  requestedOptional: ReadonlySet<ModuleSlug>,
): ReadonlyArray<ModuleSlug> {
  // Seed the resolver with (crm + messaging + requested). The resolver
  // auto-adds base modules (tenant-config, auth, rbac) and transitive
  // deps. Any slug it returns is guaranteed to be a valid ModuleSlug.
  const seed = [
    'crm' as ModuleSlug,
    'messaging' as ModuleSlug,
    ...requestedOptional,
  ];
  const result = resolveModuleActivation(seed);
  if (result.ok) return result.finalSet;
  // Fallback: foundation only. Should never trigger with the current
  // acyclic catalog.
  return FOUNDATION_SLUGS;
}

function readStep8(draft: Record<string, unknown>): Step8Draft {
  const modules = readModulesArray(draft.modules);
  // Derive requestedOptional = modules ∩ optional. Auto-deps get
  // re-derived when we recompute via the resolver, so if the persisted
  // payload includes auto-deps in `modules` we treat them as requested
  // here (they're user-visible as "on", and the resolver will classify
  // them correctly on re-render).
  const requested = new Set<ModuleSlug>();
  for (const slug of modules) {
    if (FOUNDATION_SET.has(slug)) continue;
    requested.add(slug);
  }
  return {
    requestedOptional: requested,
    dependencyValidated: draft.dependencyValidated === true,
    rationale: readRationale(draft.tenantRationale),
  };
}

function serializeStep8(next: Step8Draft): Record<string, unknown> {
  const finalSet = computeFinalSet(next.requestedOptional);
  const finalSlugSet = new Set<ModuleSlug>(finalSet);
  const rationale: Record<string, string> = {};
  for (const [slug, text] of next.rationale.entries()) {
    // Strip entries whose module isn't in the final active set (a toggle
    // could remove the module after the prospect typed a note) and
    // entries with blank text (schema would accept empty strings but the
    // persisted payload stays cleaner without them).
    if (!finalSlugSet.has(slug)) continue;
    const trimmed = text.trim();
    if (trimmed === '') continue;
    rationale[slug] = text;
  }
  const out: Record<string, unknown> = {
    modules: [...finalSet],
    tenantRationale: rationale,
  };
  // Zod's `z.literal(true)` rejects both `false` and missing — emit the
  // flag only when confirmed so the server sees "pending" as an absent
  // key rather than an explicit false.
  if (next.dependencyValidated) out.dependencyValidated = true;
  return out;
}

function groupIssuesByPath(
  issues: ReadonlyArray<{ path: string; message: string }>,
): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const issue of issues) {
    const key = issue.path === '' ? '_root' : issue.path;
    const list = out[key] ?? [];
    list.push(issue.message);
    out[key] = list;
  }
  return out;
}

export type Step8ModulesProps = IntakeShellRenderProps;

export function Step8Modules({
  stepDraft,
  onChange,
  issues,
}: Step8ModulesProps): JSX.Element {
  const errorsByPath = useMemo(() => groupIssuesByPath(issues), [issues]);
  const initial = useMemo(() => readStep8(stepDraft), [stepDraft]);

  // Local state so the rationale textareas stay controlled and
  // pre-confirmation toggles don't need a round-trip through props.
  const [requestedOptional, setRequestedOptional] = useState<ReadonlySet<ModuleSlug>>(
    () => initial.requestedOptional,
  );
  const [dependencyValidated, setDependencyValidated] = useState<boolean>(
    () => initial.dependencyValidated,
  );
  const [rationale, setRationale] = useState<Map<ModuleSlug, string>>(
    () => initial.rationale,
  );

  // Mount defaults: seed booking + payments when the draft has no modules
  // key yet. Skip when any prior draft exists so remounts never clobber.
  useEffect(() => {
    if (stepDraft.modules !== undefined) return;
    const seeded = new Set<ModuleSlug>(DEFAULT_OPTIONAL_ADDITIONS);
    setRequestedOptional(seeded);
    onChange(
      serializeStep8({
        requestedOptional: seeded,
        dependencyValidated: false,
        rationale: new Map(),
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const commit = (next: Step8Draft): void => {
    onChange(serializeStep8(next));
  };

  const finalSet = useMemo(
    () => computeFinalSet(requestedOptional),
    [requestedOptional],
  );
  const finalSlugSet = useMemo(() => new Set<ModuleSlug>(finalSet), [finalSet]);

  // Auto-activated = in finalSet but NOT foundation and NOT explicitly
  // requested. Surfaces the "also turning on" chip.
  const autoActivated = useMemo(
    () =>
      finalSet.filter(
        (slug) => !FOUNDATION_SET.has(slug) && !requestedOptional.has(slug),
      ),
    [finalSet, requestedOptional],
  );

  const handleToggle = (slug: ModuleSlug, nextChecked: boolean): void => {
    const entry = getModule(slug);
    if (!entry) return;
    if (FOUNDATION_SET.has(slug)) return; // foundation can't be toggled
    if (!isModuleImplemented(slug)) return; // #162 — coming-soon modules can never be activated (defense-in-depth behind the disabled checkbox)
    if (!nextChecked) {
      // Block deactivation if another requested-optional still depends on
      // this module. The base/foundation set is always on so their
      // dependencies are always auto-included; we only enforce across the
      // user's explicit picks.
      const active = finalSet;
      const check = validateDeactivation(active, slug);
      if (!check.ok) return;
    }
    const nextSet = new Set(requestedOptional);
    if (nextChecked) nextSet.add(slug);
    else nextSet.delete(slug);
    setRequestedOptional(nextSet);
    // Any change clears the confirmation — prospect must re-acknowledge.
    setDependencyValidated(false);
    commit({
      requestedOptional: nextSet,
      dependencyValidated: false,
      rationale,
    });
  };

  const handleConfirm = (): void => {
    setDependencyValidated(true);
    commit({
      requestedOptional,
      dependencyValidated: true,
      rationale,
    });
  };

  const handleRationaleChange = (slug: ModuleSlug, value: string): void => {
    const clipped = value.slice(0, MAX_RATIONALE_LEN);
    const next = new Map(rationale);
    if (clipped === '') next.delete(slug);
    else next.set(slug, clipped);
    setRationale(next);
    // Rationale edits do NOT reset the confirmation — they're notes, not
    // structural changes. The schema's refine only gates on the modules
    // array + dependencyValidated.
    commit({
      requestedOptional,
      dependencyValidated,
      rationale: next,
    });
  };

  const serverErrorFor = (path: string): string | null => {
    const list = errorsByPath[path];
    return list && list.length > 0 ? list.join(' · ') : null;
  };

  const modulesRootError = serverErrorFor('modules');
  const dependencyValidatedError = serverErrorFor('dependencyValidated');
  const rationaleRootError = serverErrorFor('tenantRationale');

  const confirmDisabled = dependencyValidated;
  const confirmLabel = dependencyValidated
    ? 'Selection confirmed'
    : 'Confirm my module selection';

  return (
    <form
      className="ferro-form ferro-intake__form"
      aria-label="Step 8 — Module activation"
      noValidate
      onSubmit={(e) => e.preventDefault()}
    >
      <p className="ferro-muted">
        Pick the modules you want active at launch. The first five are always
        on — they're the substrate every Animal Lovers tenant needs. Selecting
        an optional module automatically turns on anything it depends on; you
        can add or remove modules any time after launch.
      </p>

      {modulesRootError ? (
        <span
          role="alert"
          className="ferro-form__error"
          data-testid="intake-step8-modules-servererror"
        >
          {modulesRootError}
        </span>
      ) : null}

      {MODULE_GROUPS.map((group) => (
        <ModuleGroupSection
          key={group}
          group={group}
          finalSlugSet={finalSlugSet}
          requestedOptional={requestedOptional}
          finalSet={finalSet}
          onToggle={handleToggle}
        />
      ))}

      <section
        className="ferro-field ferro-intake__summary"
        aria-labelledby="intake-step8-summary-heading"
        data-testid="intake-step8-summary"
      >
        <h2
          id="intake-step8-summary-heading"
          className="ferro-field__label"
        >
          Activation summary
        </h2>
        <p className="ferro-muted" data-testid="intake-step8-summary-count">
          {finalSet.length} module{finalSet.length === 1 ? '' : 's'} will be
          active at launch ({FOUNDATION_SLUGS.length} foundation +{' '}
          {requestedOptional.size} you selected
          {autoActivated.length > 0
            ? ` + ${autoActivated.length} auto-activated dependenc${autoActivated.length === 1 ? 'y' : 'ies'}`
            : ''}
          ).
        </p>
        {autoActivated.length > 0 ? (
          <ul
            className="ferro-intake__chips"
            aria-label="Auto-activated modules"
            data-testid="intake-step8-auto-activated"
          >
            {autoActivated.map((slug) => {
              const entry = getModule(slug);
              return (
                <li
                  key={slug}
                  className="ferro-chip ferro-chip--auto"
                  data-testid={`intake-step8-auto-${slug}`}
                >
                  Auto: {entry?.displayName ?? slug}
                </li>
              );
            })}
          </ul>
        ) : null}
      </section>

      <section
        className="ferro-field ferro-intake__confirmation"
        aria-labelledby="intake-step8-confirm-heading"
      >
        <h2
          id="intake-step8-confirm-heading"
          className="ferro-field__label"
        >
          Confirm your selection
        </h2>
        <p className="ferro-muted">
          Once you confirm, we'll lock this list in for provisioning. You can
          still change modules after launch — this just signals you've
          reviewed what turns on at go-live.
        </p>
        {dependencyValidatedError ? (
          <span
            role="alert"
            className="ferro-form__error"
            data-testid="intake-step8-confirm-servererror"
          >
            {dependencyValidatedError}
          </span>
        ) : null}
        <button
          type="button"
          className="ferro-btn ferro-btn--primary"
          onClick={handleConfirm}
          disabled={confirmDisabled}
          aria-pressed={dependencyValidated}
          data-testid="intake-step8-confirm"
        >
          {confirmLabel}
        </button>
      </section>

      <section
        className="ferro-field ferro-intake__rationale"
        aria-labelledby="intake-step8-rationale-heading"
      >
        <h2
          id="intake-step8-rationale-heading"
          className="ferro-field__label"
        >
          Why each module? (optional)
        </h2>
        <p className="ferro-muted">
          Add a short note on what you expect from any module. Helps us pre-fill
          sensible defaults — skip anything you don't have an opinion on.
        </p>
        {rationaleRootError ? (
          <span
            role="alert"
            className="ferro-form__error"
            data-testid="intake-step8-rationale-servererror"
          >
            {rationaleRootError}
          </span>
        ) : null}
        <ul
          className="ferro-intake__rationaleList"
          data-testid="intake-step8-rationale-list"
        >
          {finalSet.map((slug) => {
            const entry = getModule(slug);
            const value = rationale.get(slug) ?? '';
            return (
              <li
                key={slug}
                className="ferro-intake__rationaleRow"
                data-testid={`intake-step8-rationale-${slug}`}
              >
                <label className="ferro-field">
                  <span className="ferro-field__label">
                    {entry?.displayName ?? slug}
                  </span>
                  <textarea
                    className="ferro-field__input"
                    value={value}
                    maxLength={MAX_RATIONALE_LEN}
                    rows={2}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                      handleRationaleChange(slug, e.target.value)
                    }
                    data-testid={`intake-step8-rationale-${slug}-input`}
                  />
                </label>
              </li>
            );
          })}
        </ul>
      </section>
    </form>
  );
}

type ModuleGroupSectionProps = {
  group: ModuleGroup;
  finalSlugSet: ReadonlySet<ModuleSlug>;
  requestedOptional: ReadonlySet<ModuleSlug>;
  finalSet: ReadonlyArray<ModuleSlug>;
  onToggle: (slug: ModuleSlug, next: boolean) => void;
};

function ModuleGroupSection({
  group,
  finalSlugSet,
  requestedOptional,
  finalSet,
  onToggle,
}: ModuleGroupSectionProps): JSX.Element {
  const headingId = `intake-step8-group-${group}-heading`;
  const modules = MODULE_CATALOG.filter((m) => m.group === group);
  return (
    <section
      className="ferro-intake__modulesGroup"
      aria-labelledby={headingId}
      data-testid={`intake-step8-group-${group}`}
    >
      <h2 id={headingId} className="ferro-field__label">
        {MODULE_GROUP_LABELS[group]}
      </h2>
      <p className="ferro-muted">{MODULE_GROUP_DESCRIPTIONS[group]}</p>
      <ul className="ferro-intake__modulesList">
        {modules.map((entry) => (
          <ModuleRow
            key={entry.slug}
            slug={entry.slug}
            displayName={entry.displayName}
            description={entry.description}
            dependencies={entry.dependencies}
            isFoundation={FOUNDATION_SET.has(entry.slug)}
            isBaseModule={BASE_MODULE_SLUGS.includes(entry.slug)}
            isInFinalSet={finalSlugSet.has(entry.slug)}
            isRequested={requestedOptional.has(entry.slug)}
            finalSet={finalSet}
            onToggle={onToggle}
          />
        ))}
      </ul>
    </section>
  );
}

type ModuleRowProps = {
  slug: ModuleSlug;
  displayName: string;
  description: string;
  dependencies: ReadonlyArray<ModuleSlug>;
  isFoundation: boolean;
  isBaseModule: boolean;
  isInFinalSet: boolean;
  isRequested: boolean;
  finalSet: ReadonlyArray<ModuleSlug>;
  onToggle: (slug: ModuleSlug, next: boolean) => void;
};

function ModuleRow({
  slug,
  displayName,
  description,
  dependencies,
  isFoundation,
  isBaseModule,
  isInFinalSet,
  isRequested,
  finalSet,
  onToggle,
}: ModuleRowProps): JSX.Element {
  // Foundation modules are always checked, always disabled. Optional
  // modules follow the requested flag, and the "Auto" chip surfaces when
  // a dep pulled them in without an explicit user request.
  // #162 — coming-soon modules (manifest-only stubs) are non-activatable.
  const comingSoon = !isModuleImplemented(slug);
  const checked = isFoundation ? true : comingSoon ? false : isRequested || isInFinalSet;
  const autoActivated = !isFoundation && !comingSoon && !isRequested && isInFinalSet;

  // Surface the block reason when the user unchecks a module that some
  // other active module still depends on. Mirrors the wizard's pattern.
  const blockError = useMemo(() => {
    if (isFoundation || !isRequested) return null;
    const check = validateDeactivation(finalSet, slug);
    return check.ok ? null : describeActivationError(check.error);
  }, [isFoundation, isRequested, finalSet, slug]);

  return (
    <li
      className={
        'ferro-intake__moduleRow'
        + (isFoundation ? ' ferro-intake__moduleRow--foundation' : '')
        + (autoActivated ? ' ferro-intake__moduleRow--auto' : '')
        + (comingSoon ? ' ferro-intake__moduleRow--coming-soon' : '')
      }
      data-testid={`intake-step8-row-${slug}`}
    >
      <label className="ferro-intake__moduleToggle">
        <input
          type="checkbox"
          checked={checked}
          disabled={isFoundation || comingSoon}
          readOnly={isFoundation || comingSoon}
          onChange={(e) => onToggle(slug, e.target.checked)}
          data-testid={`intake-step8-toggle-${slug}`}
          aria-describedby={`intake-step8-desc-${slug}`}
        />
        <span className="ferro-intake__moduleName">{displayName}</span>
        {comingSoon ? (
          <span
            className="ferro-chip ferro-chip--coming-soon"
            data-testid={`intake-step8-coming-soon-${slug}`}
          >
            Coming soon
          </span>
        ) : null}
        {isFoundation ? (
          <span
            className={
              'ferro-chip ferro-chip--foundation'
              + (isBaseModule ? ' ferro-chip--base' : '')
            }
            data-testid={`intake-step8-foundation-${slug}`}
          >
            {isBaseModule ? 'Base' : 'Foundation'}
          </span>
        ) : null}
        {autoActivated ? (
          <span
            className="ferro-chip ferro-chip--auto"
            data-testid={`intake-step8-row-auto-${slug}`}
          >
            Auto
          </span>
        ) : null}
      </label>
      <p
        id={`intake-step8-desc-${slug}`}
        className="ferro-muted"
        data-testid={`intake-step8-desc-${slug}`}
      >
        {description}
      </p>
      {dependencies.length > 0 ? (
        <ul
          className="ferro-intake__moduleDeps"
          aria-label={`Dependencies of ${displayName}`}
          data-testid={`intake-step8-deps-${slug}`}
        >
          {dependencies.map((dep) => {
            const depEntry = getModule(dep);
            return (
              <li
                key={dep}
                className="ferro-chip ferro-chip--dep"
                data-testid={`intake-step8-dep-${slug}-${dep}`}
              >
                Needs: {depEntry?.displayName ?? dep}
              </li>
            );
          })}
        </ul>
      ) : null}
      {blockError ? (
        <span
          role="alert"
          className="ferro-form__error"
          data-testid={`intake-step8-error-${slug}`}
        >
          {blockError}
        </span>
      ) : null}
    </li>
  );
}
